/**
 * 🪧 **Which street names fit on the map, and which have to go** — T200.
 *
 * 🎯 **haugur, 2026-09-14:** *"if i zoom close enough i like to see the streets
 * with names."*
 *
 * ## 🔴 Why this is a module of its own
 *
 * **9,162 distinct street names over 58,243 segments.** Drawing every name a
 * view contains puts *Hverfisgata* on top of *Laugavegur* on top of *Snorrabraut*
 * and the map becomes less readable with the names than without them. ⚠️ **A
 * label that cannot be read is worse than no label** — it also hides the street
 * underneath it.
 *
 * ✅ **Two rules, and the first one does most of the work:**
 *
 * 1. 🔴 **One label per NAME, never per segment.** That decision is made on the
 *    server, in `mapdetail.ts` — the same answer `roads.ts` reached with
 *    `DISTINCT ON (nafnfitju)` for the heads-up display, because **a name over a
 *    street is one problem and it already had an answer.**
 * 2. **Then drop whatever still collides**, longest street first, so what
 *    survives is what somebody is most likely to be hunting for.
 *
 * ## ⚠️ The text is MEASURED BY ESTIMATE, and that is deliberate
 *
 * A real measurement means `getBBox()` on an SVG text node, which forces a
 * layout **per candidate, per frame, while the map is being dragged**. At a
 * hundred candidates on a machine that draws at 2 fps that is the whole budget.
 * 🔴 **So the width is estimated from the character count**, generously, and a
 * slightly-too-wide estimate costs one label rather than a stutter.
 *
 * ⚠️ **Icelandic matters here**: *Þ, Æ, Ö, ð* are ordinary-width letters, but
 * the names are long — *Tjarnarbraut*, *Reykjavíkurvegur*, *Álfaskeið* — so the
 * estimate is per character and not a fixed box.
 */

/** A street that would like its name drawn, in ISN93 metres. */
export interface LabelCandidate {
  readonly name: string;
  readonly x: number;
  readonly y: number;
  /** Metres of this street inside the view. Longer wins when two collide. */
  readonly metres: number;
}

/** A name that survived, with where it goes in ISN93 metres. */
export interface PlacedLabel {
  readonly name: string;
  readonly x: number;
  readonly y: number;
}

/**
 * How wide one character is as a fraction of the font size.
 *
 * Alegreya Sans at 0.52 em average is a little generous for lower case and
 * about right for a name in mixed case. **Generous on purpose** — see above.
 */
const CHAR_WIDTH = 0.52;

/** Breathing room around each label, in pixels, so two never quite touch. */
const PAD = 3;

/**
 * 🔴 **The most names worth drawing at once.**
 *
 * Not a performance limit — a legibility one. A view with forty street names
 * in it is a view somebody has to read forty things off, and the collision rule
 * will usually have cut it below this anyway.
 */
const MAX_LABELS = 40;

/** A rectangle in the picture's own pixels. */
export interface LabelBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The box a piece of centred text occupies, from its ANCHOR.
 *
 * ⚠️ An SVG `text` y is a **baseline**, not a middle: the letters sit above
 * the point and only the descenders hang below. A box centred on the anchor
 * would sit half a line too low and let two names touch.
 */
export function textBox(px: number, py: number, text: string, fontPx: number): LabelBox {
  const halfWidth = (text.length * fontPx * CHAR_WIDTH) / 2;
  return {
    left: px - halfWidth,
    right: px + halfWidth,
    top: py - fontPx * 0.82,
    bottom: py + fontPx * 0.22,
  };
}

function overlaps(a: LabelBox, b: LabelBox): boolean {
  return !(a.right < b.left || b.right < a.left || a.bottom < b.top || b.bottom < a.top);
}

/**
 * Choose the street names that fit.
 *
 * @param toPixel ISN93 metres → pixels within the picture.
 * @param view the size of the picture, in pixels.
 * @param fontPx how big the labels are drawn, in pixels.
 * @param limit the most names to place.
 * @param reserved boxes already taken — 🔴 **the PLACE names.**
 * ⚠️ Without this a street name draws straight through *Hafnarfjörður*.
 * The two sets are drawn in different layers and culling each set against
 * itself looks finished and is not; **found on a baked picture of Reykjavík,
 * where *Efstasund* ran through the city's own name.** The place names go down
 * first because there are few of them and they are what tells you where you
 * are.
 */
export function placeLabels(
  candidates: readonly LabelCandidate[],
  toPixel: (x: number, y: number) => { px: number; py: number },
  view: { readonly w: number; readonly h: number },
  fontPx: number,
  limit: number = MAX_LABELS,
  reserved: readonly LabelBox[] = [],
): PlacedLabel[] {
  /* Longest first. The server already sorts, but a caller that filters or
     concatenates must not be able to silently change which names survive. */
  const ordered = [...candidates].sort((a, b) => b.metres - a.metres);

  const taken: LabelBox[] = [...reserved];
  const placed: PlacedLabel[] = [];

  for (const candidate of ordered) {
    if (placed.length >= limit) break;
    if (candidate.name.length === 0) continue;

    const { px, py } = toPixel(candidate.x, candidate.y);
    const raw = textBox(px, py, candidate.name, fontPx);

    /* 🔴 **The WHOLE name has to fit, not just its middle.**
       ⚠️ Two faults, and both were on the first baked picture: a label centred
       just inside the right edge **drew half a street name**, and a label off
       the picture altogether would otherwise have to be clamped — which sits a
       name somewhere its street is not, and on a map nothing tells you that by
       looking. **Dropped, both ways.** */
    if (raw.left < 0 || raw.right > view.w) continue;
    if (raw.top < 0 || raw.bottom > view.h) continue;

    const box: LabelBox = {
      left: raw.left - PAD,
      right: raw.right + PAD,
      top: raw.top - PAD,
      bottom: raw.bottom + PAD,
    };

    if (taken.some((other) => overlaps(box, other))) continue;
    taken.push(box);
    placed.push({ name: candidate.name, x: candidate.x, y: candidate.y });
  }

  return placed;
}
